import { Dropdown } from 'antd'
import { useState, type CSSProperties } from 'react'
import { MdCheck, MdExpandMore } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'

import styles from './StatusBadge.module.css'

export interface StatusOption {
    value: string
    label: string
    color?: string
}

const DEFAULT_COLOR = '#8c8c8c'

/**
 * Значення, при виборі яких меню підсвічує пункт як небезпечний (наприклад, видалення).
 * Передається з поля ресурсу через confirmOnValues.
 */
interface StatusBadgeProps {
    option: StatusOption | undefined
    /** Підпис, якщо опцію не знайдено серед options */
    fallbackLabel?: string
    className?: string
}

/** Статичний бейдж статусу: кольорова крапка + підпис у м'якій пігулці */
export const StatusBadge = ({ option, fallbackLabel, className }: StatusBadgeProps) => {
    const color = option?.color ?? DEFAULT_COLOR
    return (
        <span
            className={`${cx(styles, 'badge')} ${className ?? ''}`}
            style={{ '--status-color': color } as CSSProperties}
        >
            <i className={cx(styles, 'dot')} aria-hidden="true" />
            <span className={cx(styles, 'label')}>{option?.label ?? fallbackLabel ?? '—'}</span>
        </span>
    )
}

interface StatusChangerProps {
    value: string
    options: StatusOption[]
    /** Виклик після вибору нового статусу; поки Promise не завершиться, бейдж у стані busy */
    onChange: (option: StatusOption) => void | Promise<void>
    /** Значення, які позначаються як небезпечні у меню */
    dangerValues?: string[]
    /** Без права зміни показуємо статичний бейдж */
    editable?: boolean
    className?: string
}

/**
 * Бейдж статусу з меню зміни: пігулка з крапкою і шевроном, меню з кольоровими
 * пунктами, поточний позначено галочкою. Логіка запиту живе у виклику onChange.
 */
export const StatusChanger = ({
    value,
    options,
    onChange,
    dangerValues = [],
    editable = true,
    className,
}: StatusChangerProps) => {
    const [open, setOpen] = useState(false)
    const [busy, setBusy] = useState(false)

    const current = options.find((option) => option.value === value)
    const color = current?.color ?? DEFAULT_COLOR

    if (!editable) return <StatusBadge option={current} fallbackLabel={value} className={className ?? ''} />

    const select = async (option: StatusOption) => {
        setOpen(false)
        if (option.value === value) return
        setBusy(true)
        try {
            await onChange(option)
        } finally {
            setBusy(false)
        }
    }

    const menu = (
        <div className={cx(styles, 'menu')} role="menu" aria-label="Змінити статус">
            <div className={cx(styles, 'menuTitle')}>Змінити статус</div>
            {options.map((option) => {
                const isCurrent = option.value === value
                const isDanger = dangerValues.includes(option.value)
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={isCurrent}
                        className={`${cx(styles, 'option')} ${isCurrent ? cx(styles, 'optionCurrent') : ''} ${isDanger ? cx(styles, 'optionDanger') : ''}`}
                        style={{ '--status-color': option.color ?? DEFAULT_COLOR } as CSSProperties}
                        onClick={() => void select(option)}
                    >
                        <i className={cx(styles, 'dot')} aria-hidden="true" />
                        <span className={cx(styles, 'optionLabel')}>{option.label}</span>
                        {isCurrent && <MdCheck className={cx(styles, 'optionCheck')} aria-hidden="true" />}
                    </button>
                )
            })}
        </div>
    )

    return (
        <Dropdown
            trigger={['click']}
            open={open}
            onOpenChange={(next) => !busy && setOpen(next)}
            popupRender={() => menu}
            placement="bottomLeft"
        >
            <button
                type="button"
                className={`${cx(styles, 'badge')} ${cx(styles, 'trigger')} ${open ? cx(styles, 'triggerOpen') : ''} ${busy ? cx(styles, 'triggerBusy') : ''} ${className ?? ''}`}
                style={{ '--status-color': color } as CSSProperties}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-busy={busy}
                disabled={busy}
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
            >
                <i className={cx(styles, 'dot')} aria-hidden="true" />
                <span className={cx(styles, 'label')}>{current?.label ?? value}</span>
                <MdExpandMore className={cx(styles, 'chevron')} aria-hidden="true" />
            </button>
        </Dropdown>
    )
}
